import React, { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { IoClose } from "react-icons/io5";
import { format, parseISO } from "date-fns";
import { getOrdersByUser } from "../../Api/Api";

const MyOrdersModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("pending");
  const [user, setUser] = useState(() => {
    // safely parse user from localStorage
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);

    getOrdersByUser(user.id)
      .then((res) => {
        setOrders(res);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load orders");
        setLoading(false);
      });
  }, [user]);

  // Use your actual statuses: "pending" and "confirmed"
  const pendingOrders = orders.filter((order) => order.status === "pending");
  const completedOrders = orders.filter((order) => order.status === "confirmed");

  const ordersToShow = activeTab === "pending" ? pendingOrders : completedOrders;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 left-0 w-full max-w-md bg-white shadow-xl transition-all duration-300 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-[#0D004C]">My Orders</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-black">
            <IoClose size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b text-sm font-medium">
          <button
            onClick={() => setActiveTab("pending")}
            className={`w-1/2 py-2 ${
              activeTab === "pending"
                ? "border-b-2 border-[#0D004C] text-[#0D004C]"
                : "text-gray-500"
            }`}
          >
            Pending Orders
          </button>
          <button
            onClick={() => setActiveTab("completed")}
            className={`w-1/2 py-2 ${
              activeTab === "completed"
                ? "border-b-2 border-[#0D004C] text-[#0D004C]"
                : "text-gray-500"
            }`}
          >
            Completed Orders
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[80vh] flex-1">
          {loading ? (
            <p className="text-center text-gray-500">Loading orders...</p>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : ordersToShow.length > 0 ? (
            ordersToShow.map((order) => (
              <div
                key={order.id}
                className="border rounded-lg p-4 shadow-sm bg-white"
              >
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div className="flex flex-wrap gap-1 max-w-[70%]">
                    {order.services?.map((service) => (
                      <span
                        key={service.id}
                        className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs"
                      >
                        {service.name}
                      </span>
                    )) || "Service"}
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      order.status === "pending"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>

                <div className="text-sm text-gray-600 mt-2 space-y-1">
                  <p>
                    <span className="font-medium">Order Date:</span>{" "}
                    {format(parseISO(order.created_at), "dd MMM yyyy")}
                  </p>
                  <p>
                    <span className="font-medium">Arriving Date:</span>{" "}
                    {format(parseISO(order.delivery_date), "dd MMM yyyy")}
                  </p>
                  <p>
                    <span className="font-medium">Payment Status:</span>{" "}
                    {order.payment_status.charAt(0).toUpperCase() +
                      order.payment_status.slice(1)}
                  </p>
                  <p>
                    <span className="font-medium">Total:</span> ₹{order.total}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500 text-center">No orders found.</p>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default MyOrdersModal;
